import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "react-oidc-context";
import { useNavigate } from 'react-router-dom';
import { FileText, Link, Database, FileStack, TrendingUp } from 'lucide-react';
import { StatCard } from './common/StatCard';
import { ChangeCard } from './common/ChangeCard';
import { PartnerTable } from './common/PartnerTable';
import { PPIMAPI } from '../../services/api/ppimAPI';
import { generateSnapshotOptionsFrom } from '../../utils/date_utils';
import { setSnapshot, setPartnerSummary, setOverviewStats } from "../../store/appSlice";
import { toast } from 'react-toastify';

const PartnerOverview = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { snapshot, partnerSummary, partnerSummarySnapshot, overviewStats, overviewStatsSnapshot } = useSelector((state) => state.app);

    const snapshotOptions = generateSnapshotOptionsFrom(11, 2025);
    const auth = useAuth();

    const token = auth.user?.access_token;
    const api = useMemo(() => PPIMAPI(token), [token]);

    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const sortedPartnerData = useMemo(() => {
        if (!sortConfig.key) return partnerSummary;
        return [...partnerSummary].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [partnerSummary, sortConfig]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const onChangeCardClick = (targetTab, targetFilter) => {
        navigate(`/partner?tab=${targetTab}&partner=All Partners&new=${targetFilter}`);
    };

    const handlePartnerClick = (partnerName) => {
        navigate(`/partner?partner=${encodeURIComponent(partnerName)}&tab=dcus`);
    };

    // Load dashboard data; re-runs on token refresh or snapshot change
    useEffect(() => {
        if (!token || !snapshot) return;

        // Skip fetch if Redux already has data for this snapshot
        if (partnerSummarySnapshot === snapshot && partnerSummary.length > 0 && overviewStats && overviewStatsSnapshot === snapshot) {
            setLoading(false);
            return;
        }
        loadDashboardData(snapshot);
    }, [token, snapshot]);

    const loadDashboardData = async (selectedSnapshot) => {
        if (!token) return;

        setLoading(true);
        try {
            const [stats, partners] = await Promise.all([
                api.getOverviewStats(selectedSnapshot),
                api.getPartnerData(selectedSnapshot)
            ]);

            dispatch(setOverviewStats({ data: stats, snapshot: selectedSnapshot }));
            dispatch(setPartnerSummary({ data: partners, snapshot: selectedSnapshot }));

            localStorage.setItem('savedSnapshot', selectedSnapshot);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load overview data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSnapshotSelect = (value) => {
        dispatch(setSnapshot(value));
    };

    return (
        <>
            <div className="flex items-start justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Partner Overview</h2>
                    <p className="text-sm text-gray-600">
                        Monitor partner compliance and data collection performance
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Snapshot:</label>
                    <select
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={snapshot || ''}
                        onChange={(e) => handleSnapshotSelect(e.target.value)}
                    >
                        {snapshotOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading && (
                <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}

            {!loading && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <StatCard
                            title="Total Partners"
                            value={overviewStats?.totalPartners.value}
                            change={overviewStats?.totalPartners.change}
                            icon={FileText}
                        />

                        <StatCard
                            title="Total DCUs"
                            value={overviewStats?.totalDCUs.value.toLocaleString()}
                            subtitle={`${overviewStats?.totalDCUs.connected} connected`}
                            change={overviewStats?.totalDCUs.change}
                            icon={Link}
                        />

                        <StatCard
                            title="DCU Connection Rate"
                            value={overviewStats?.dcuConnectionRate.value}
                            subtitle={`Latest scan: ${overviewStats?.dcuConnectionRate.lastScan}`}
                            change={`${overviewStats?.dcuConnectionRate.statusChanges} status changes`}
                            icon={Database}
                        />

                        <StatCard
                            title="Consumer Records"
                            value={overviewStats?.consumerRecords.value}
                            subtitle={overviewStats?.consumerRecords.period}
                            change={`Latest: ${overviewStats?.consumerRecords.latest.split('T')[0]}`}
                            icon={FileStack}
                        />
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            Month-over-Month Changes
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <ChangeCard
                                label="New DCUs"
                                value={overviewStats?.monthOverMonth.newDCUs}
                                icon={Link}
                                bgColor="bg-blue-50"
                                badgeColor="bg-blue-100 text-blue-700"
                                isClickable={true}
                                onClick={() => onChangeCardClick('dcus', 'dcus')}
                            />

                            <ChangeCard
                                label="Connection Changes"
                                value={overviewStats?.monthOverMonth.connectionChanges}
                                icon={Database}
                                bgColor="bg-yellow-50"
                                badgeColor="bg-yellow-100 text-yellow-700"
                                isClickable={true}
                                onClick={() => onChangeCardClick('dcus', 'connection_change')}
                            />

                            <ChangeCard
                                label="New PPUs"
                                value={overviewStats?.monthOverMonth.newPPUs}
                                icon={FileText}
                                bgColor="bg-orange-50"
                                badgeColor="bg-orange-100 text-orange-700"
                                isClickable={true}
                                onClick={() => onChangeCardClick('dcus', 'ppus')}
                            />

                            <ChangeCard
                                label="New Privacy Policies"
                                value={overviewStats?.monthOverMonth.newPrivacyPolicies}
                                icon={FileStack}
                                bgColor="bg-cyan-50"
                                badgeColor="bg-cyan-100 text-cyan-700"
                                isClickable={true}
                                onClick={() => onChangeCardClick('policies', 'policies')}
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">Partner Summary</h3>
                            <p className="text-sm text-gray-600">
                                Click on partner name to view detailed profile
                            </p>
                        </div>

                        <PartnerTable
                            data={sortedPartnerData}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onPartnerClick={handlePartnerClick}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default PartnerOverview;